import { registerPostRender } from '../core/render.js';

// ════════════════════════════════════════════════════════════════════════
// JOURNEY SCROLL-DRAW (Track B · Ola 1.3) — motor del trazo atado al scroll.
//
// Un único motor para las 3 variantes (v1 serpenteante / v2 pinned horizontal /
// v3 espina recta). NO hardcodea la forma: lee las posiciones reales de los
// puntos (.journey-dot) ya colocados por CSS y genera el path que los conecta
// (spline Catmull-Rom). Así es robusto ante nº dinámico de pasos y responsive.
//
// Técnica de dibujo: stroke-dasharray/strokeDashoffset (lo mismo que manipula
// anime.createDrawable) atado al progreso de scroll → barato, sin re-tesela.
// Se registra previewOnly (corre en preview + público, no en edición) y un
// hook editorOnly que deja el trazo completo/estático mientras se edita.
// Importado desde svg-draw.js para cargar en ambos bundles (editor y público).
// ════════════════════════════════════════════════════════════════════════

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

let instances = [];
let rafId = 0;
let bound = false;

/** Spline suave (Catmull-Rom → Bézier) que pasa por todos los puntos. */
function smoothPath(pts) {
    if (pts.length < 2) return '';
    if (pts.length === 2) return `M${pts[0].x},${pts[0].y} L${pts[1].x},${pts[1].y}`;
    let d = `M${pts[0].x},${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] || p2;
        const c1x = p1.x + (p2.x - p0.x) / 6;
        const c1y = p1.y + (p2.y - p0.y) / 6;
        const c2x = p2.x - (p3.x - p1.x) / 6;
        const c2y = p2.y - (p3.y - p1.y) / 6;
        d += ` C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`;
    }
    return d;
}

function buildInstance(section) {
    const track = section.querySelector('[data-journey-track]');
    const svg = section.querySelector('.journey-svg');
    const rail = section.querySelector('.journey-rail');
    const route = section.querySelector('.journey-route');
    const tip = section.querySelector('.journey-tip');
    const steps = Array.from(section.querySelectorAll('.journey-step'));
    const dots = steps.map(s => s.querySelector('.journey-dot')).filter(Boolean);
    if (!track || !svg || !route || steps.length === 0 || dots.length !== steps.length) return null;
    return {
        section, track, svg, rail, route, tip, steps, dots,
        variant: section.getAttribute('data-journey-variant') || 'v1',
        len: 0, fractions: [],
    };
}

/** Mide el contenedor + los puntos, fija el viewBox y genera el path. */
function layout(inst) {
    const { track, svg, rail, route, dots } = inst;
    const W = track.clientWidth, H = track.clientHeight;
    if (!W || !H) return;
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);

    const tr = track.getBoundingClientRect();
    const pts = dots.map(d => {
        const r = d.getBoundingClientRect();
        return { x: r.left + r.width / 2 - tr.left, y: r.top + r.height / 2 - tr.top };
    });

    if (pts.length < 2) {
        route.setAttribute('d', '');
        if (rail) rail.setAttribute('d', '');
        inst.len = 0;
        inst.fractions = pts.length ? [0] : [];
        return;
    }

    const d = smoothPath(pts);
    if (rail) rail.setAttribute('d', d);
    route.setAttribute('d', d);

    const len = route.getTotalLength ? route.getTotalLength() : 0;
    inst.len = len;
    if (len) {
        route.style.strokeDasharray = `${len}`;
    }

    // Fracción de cada punto a lo largo del trazo (distancia acumulada) para
    // que cada hito se encienda justo cuando la punta del trazo lo alcanza.
    const cum = [0];
    for (let i = 1; i < pts.length; i++) {
        cum[i] = cum[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    }
    const total = cum[cum.length - 1] || 1;
    inst.fractions = cum.map(c => c / total);
}

/** Progreso de scroll 0→1 del trazo de esta instancia. */
function progressFor(inst) {
    const vh = window.innerHeight || 1;
    if (inst.variant === 'v2') {
        // Pinned: progreso = cuánto se ha desplazado dentro de la sección alta.
        const r = inst.section.getBoundingClientRect();
        const scrollable = r.height - vh;
        // Solo si está REALMENTE pinned (sección alta). En móvil/editor el pin
        // se desactiva por CSS → la sección es baja → caemos al cálculo vertical.
        if (scrollable > vh * 0.4) return clamp(-r.top / scrollable, 0, 1);
    }
    // Vertical: dibuja conforme la pista atraviesa el viewport.
    const r = inst.track.getBoundingClientRect();
    return clamp((vh * 0.78 - r.top) / ((r.height * 0.66) || 1), 0, 1);
}

function apply(inst, p) {
    const { route, tip, steps, fractions, len } = inst;
    if (len) route.style.strokeDashoffset = `${len * (1 - p)}`;

    if (tip && len) {
        if (p > 0.012 && p < 0.985) {
            const pt = route.getPointAtLength(len * p);
            tip.setAttribute('cx', pt.x.toFixed(1));
            tip.setAttribute('cy', pt.y.toFixed(1));
            tip.style.opacity = '1';
        } else {
            tip.style.opacity = '0';
        }
    }

    const last = steps.length - 1 || 1;
    steps.forEach((s, i) => {
        const at = fractions[i] != null ? fractions[i] : i / last;
        s.classList.toggle('is-active', p >= at - 0.02);
    });
}

function setStatic(inst) {
    if (inst.len) inst.route.style.strokeDashoffset = '0';
    inst.steps.forEach(s => s.classList.add('is-active'));
    if (inst.tip) inst.tip.style.opacity = '0';
}

function update() {
    rafId = 0;
    const vh = window.innerHeight || 1;
    for (const inst of instances) {
        const r = inst.section.getBoundingClientRect();
        if (r.bottom < -240 || r.top > vh + 240) continue; // fuera de viewport: no trabajar
        apply(inst, progressFor(inst));
    }
}

function onScroll() { if (!rafId) rafId = requestAnimationFrame(update); }
function onResize() { instances.forEach(layout); if (!rafId) rafId = requestAnimationFrame(update); }

function teardown() {
    if (bound) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        bound = false;
    }
    if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
    instances = [];
}

function collect() {
    teardown();
    instances = Array.from(document.querySelectorAll('.journey-layout'))
        .map(buildInstance)
        .filter(Boolean);
    instances.forEach(layout);
}

/** preview + público: trazo atado al scroll. */
export function initJourneyScrollDraw() {
    collect();
    if (!instances.length) return;

    if (prefersReducedMotion()) {
        instances.forEach(setStatic);
        instances = [];
        return;
    }

    instances.forEach(inst => apply(inst, progressFor(inst)));
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    bound = true;

    // Re-medir tras asentarse fuentes/imágenes (cambian la altura → posiciones).
    setTimeout(() => { instances.forEach(layout); update(); }, 320);
}

/** editor: trazo completo y estático (sin scrubbing) para editar cómodo. */
export function initJourneyStatic() {
    collect();
    instances.forEach(setStatic);
    instances = [];
}

registerPostRender(initJourneyScrollDraw, { previewOnly: true });
registerPostRender(initJourneyStatic, { editorOnly: true });
